import Alert from '../components/common/Alert';
import { shipmentService } from '../services/shipmentService';
import {
  getReceiptSizeForEvent,
  RECEIPT_LAYOUT_LABELS,
  shouldAutoPrintReceipt,
} from './networkMode';
import { isHandheldDevice, printHtmlOnHandheld } from './handheldFile.js';

function triggerPrint(target) {
  try {
    target?.focus();
    target?.print();
    return true;
  } catch {
    return false;
  }
}

function whenDocumentReady(doc, onReady) {
  let fired = false;
  const finish = () => {
    if (fired) return;
    fired = true;
    onReady();
  };
  const images = Array.from(doc?.images || []);
  if (!images.length) {
    window.setTimeout(finish, 250);
    return;
  }
  let pending = images.length;
  const mark = () => {
    pending -= 1;
    if (pending <= 0) finish();
  };
  images.forEach((img) => {
    if (img.complete) mark();
    else {
      img.addEventListener('load', mark, { once: true });
      img.addEventListener('error', mark, { once: true });
    }
  });
  window.setTimeout(finish, 1200);
}

export function printHtmlInFrame(html, title) {
  if (isHandheldDevice()) {
    return printHtmlOnHandheld(html, title);
  }
  return new Promise((resolve) => {
    const iframe = document.createElement('iframe');
    iframe.setAttribute('title', title);
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText =
      'position:fixed;left:-10000px;top:0;width:1200px;height:1600px;border:0;opacity:1';
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (!doc) {
      iframe.remove();
      resolve(false);
      return;
    }
    doc.open();
    doc.write(html);
    doc.close();
    const remove = () => {
      try {
        iframe.remove();
      } catch {
        /* ignore */
      }
    };
    whenDocumentReady(doc, () => {
      try {
        iframe.contentWindow?.addEventListener('afterprint', () => {
          window.setTimeout(remove, 300);
        });
      } catch {
        /* ignore */
      }
      const printed = triggerPrint(iframe.contentWindow);
      if (!printed) remove();
      else window.setTimeout(remove, 120_000);
      resolve(printed);
    });
  });
}

function headerLayout(response) {
  return String(response?.headers?.['x-receipt-layout'] || '').toLowerCase();
}

/** Open shipment receipt HTML and trigger browser print dialog. */
export async function printShipmentReceipt(shipmentId, user, { silent = false, event, layout } = {}) {
  if (!shipmentId) return false;
  if (silent && !shouldAutoPrintReceipt(user, event) && !layout) {
    return false;
  }
  try {
    const response = await shipmentService.printReceipt(shipmentId, event, {
      force: !silent || Boolean(layout),
      layout,
    });
    if (response?.status === 204 || headerLayout(response) === 'off') {
      return false;
    }
    const usedLayout = headerLayout(response) || layout || getReceiptSizeForEvent(user, event);
    const html = response.data;
    const printed = await printHtmlInFrame(html, `Receipt ${shipmentId}`);
    if (!printed) {
      if (!silent) Alert.error('Could not open the print dialog');
      return false;
    }
    if (!silent) {
      const label = RECEIPT_LAYOUT_LABELS[usedLayout] || 'Large';
      Alert.success(`${label} receipt sent to print`);
    }
    return true;
  } catch (error) {
    if (!silent) {
      Alert.error(error.response?.data?.error || 'Failed to print receipt');
    }
    return false;
  }
}

/**
 * After booking: navigate first, then print receipt (+ QR) so the shipments
 * list can load. print() blocks the UI, so we delay until after navigation.
 */
export function scheduleBookingPrintJobs(shipmentId, user, { delayMs = 900 } = {}) {
  if (!shipmentId) return;
  window.setTimeout(() => {
    void (async () => {
      try {
        await printShipmentReceipt(shipmentId, user, { silent: true, event: 'booking' });
      } catch {
        /* ignore */
      }
      try {
        const { maybePrintQrWithBookingReceipt } = await import('./shipmentQrPrint.js');
        await maybePrintQrWithBookingReceipt(user, shipmentId);
      } catch {
        /* ignore */
      }
    })();
  }, delayMs);
}

const MANUAL_LAYOUTS = ['large', 'short', 'legal', 'laser3', 'thermal'];

export async function printShipmentReceiptChoice(shipmentId, user, layout, event) {
  if (layout === 'all') {
    for (const id of MANUAL_LAYOUTS) {
      // eslint-disable-next-line no-await-in-loop
      await printShipmentReceipt(shipmentId, user, { event, layout: id, silent: true });
    }
    Alert.success('All receipt layouts sent to print');
    return true;
  }
  return printShipmentReceipt(shipmentId, user, { event, layout });
}

export async function printShipmentReceipts(shipmentIds, user) {
  const ids = [...new Set((shipmentIds || []).filter(Boolean))];
  for (const id of ids) {
    // eslint-disable-next-line no-await-in-loop
    await printShipmentReceipt(id, user, { silent: ids.length > 1, event: 'delivery' });
  }
}
